import { useEffect, useState } from 'react';
import { Play, Volume2 } from 'lucide-react';
import audioService from '../../services/audioService';
import {
  persistNotificationSoundPref,
  readNotificationSoundPrefs,
} from '../../utils/notificationSoundPrefs';
import { SettingsSwitch, SettingsVolumeSlider } from './settingsUi.jsx';

/**
 * Contrôles sonores ACTIVE NOW.
 * Tester utilise audioService.play(true) — même pipeline que l'événement métier.
 * Son OFF : Tester désactivé, aucun playback réel.
 */
export default function SoundPreferencesControls({
  commercantId = null,
  showRepeat = false,
  enableLabel = 'Activer le son dans l\'admin',
  variant = 'merchant',
}) {
  const [prefs, setPrefs] = useState(() => readNotificationSoundPrefs(commercantId));
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const next = readNotificationSoundPrefs(commercantId);
    setPrefs(next);
    audioService.setEnabled(next.enabled);
    audioService.setVolume(next.volume);
  }, [commercantId]);

  const updatePref = (key, value) => {
    persistNotificationSoundPref(commercantId, key, value);
    setPrefs((prev) => ({ ...prev, [key]: value }));
    if (key === 'enabled') audioService.setEnabled(value);
    if (key === 'volume') audioService.setVolume(value);
  };

  const handleTest = async () => {
    if (!prefs.enabled || testing) return;
    setTesting(true);
    try {
      audioService.stopRepeat();
      audioService.setEnabled(true);
      audioService.setVolume(prefs.volume);
      audioService.resetCooldown();
      await audioService.play(true);
    } catch (error) {
      console.error('[Settings sound test]', error);
    } finally {
      setTimeout(() => setTesting(false), 800);
    }
  };

  const volumeAndTest = (
    <>
      <div>
        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-gray-600">
          <Volume2 className="h-4 w-4 text-gray-400" />
          Volume
        </div>
        <SettingsVolumeSlider
          value={prefs.volume}
          onChange={(next) => updatePref('volume', next)}
          disabled={!prefs.enabled}
        />
      </div>

      {showRepeat ? (
        <div className="space-y-2">
          <label className="block text-[13px] font-medium text-gray-600">Répétition</label>
          <select
            value={prefs.repeatMode}
            onChange={(e) => updatePref('repeatMode', e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="unique">Sonnerie unique</option>
            <option value="repeat">Répéter jusqu’à action</option>
          </select>
          {prefs.repeatMode === 'repeat' ? (
            <div>
              <p className="mb-1 text-xs text-gray-500">Intervalle entre chaque répétition</p>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="0.5"
                  value={prefs.repeatInterval}
                  onChange={(e) => updatePref('repeatInterval', parseFloat(e.target.value))}
                  className="settings-volume-slider flex-1"
                />
                <span className="w-10 text-right text-sm font-medium text-gray-700">{prefs.repeatInterval}s</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        onClick={handleTest}
        disabled={!prefs.enabled || testing}
        className="inline-flex items-center gap-2 rounded-xl border border-primary-200 bg-white px-3.5 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Play className="h-4 w-4" />
        {testing ? 'Lecture…' : 'Tester le son'}
      </button>
    </>
  );

  if (variant === 'platform') {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-gray-900">{enableLabel}</p>
          <SettingsSwitch
            checked={prefs.enabled}
            onChange={(next) => updatePref('enabled', next)}
            label={enableLabel}
          />
        </div>
        <div className={`space-y-4 ${prefs.enabled ? '' : 'pointer-events-none opacity-50'}`}>
          {volumeAndTest}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{enableLabel}</p>
          <p className="text-xs text-gray-500">Même son que les nouvelles commandes, RDV et réservations.</p>
        </div>
        <SettingsSwitch
          checked={prefs.enabled}
          onChange={(next) => updatePref('enabled', next)}
          label={enableLabel}
        />
      </div>

      <div className={`space-y-4 ${prefs.enabled ? '' : 'pointer-events-none opacity-50'}`}>
        {volumeAndTest}
      </div>
    </div>
  );
}
